import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import RegisterAdmin from './pages/RegisterAdmin';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import CreateBot from './pages/CreateBot';
import UpdateBot from './pages/UpdateBot';
import WelcomeMessage from './pages/WelcomeMessage';
import PaymentPlans from './pages/PaymentPlans';
import RedirectButtons from './pages/RedirectButtons';
import BotCommands from './pages/BotCommands';
import BotAdministrators from './pages/BotAdministrators';
import BotTelegramGroups from './pages/BotTelegramGroups';
import BotFather from './pages/BotFather';
import Marketing from './pages/Marketing';
import Alerts from './pages/Alerts';
import Downsell from './pages/Downsell';
import Contacts from './pages/Contacts';
import ContactDetails from './pages/ContactDetails';
import Users from './pages/Users';
import Logs from './pages/Logs';
import PaymentCycles from './pages/PaymentCycles';
import PaymentGatewayConfigs from './pages/PaymentGatewayConfigs';
import GroupManagement from './pages/GroupManagement';
import Billing from './pages/Billing';
import UserGroups from './pages/UserGroups';
import SecuritySettings from './pages/SecuritySettings';
import FtpManager from './pages/FtpManager';
import CardPayment from './pages/CardPayment';
import StorageSettings from './pages/StorageSettings';
import ArtisanCommands from './pages/ArtisanCommands';
import CronJobs from './pages/CronJobs';
import LaravelLogs from './pages/LaravelLogs';
import ManageBot from './pages/ManageBot';
import BotList from './pages/BotList';
import PaymentStatus from './pages/PaymentStatus';
import Profile from './pages/Profile';
import WarningMessages from './pages/WarningMessages';
import AdminCommands from './pages/AdminCommands';
import Translations from './pages/Translations';
import TrackingLinks from './pages/TrackingLinks';
import NotFound from './pages/NotFound';
import './App.css';

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <ThemeProvider>
      <AuthProvider>
        <ErrorBoundary>
          <Router>
            <div className="App">
              <Routes>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/register-admin" element={<RegisterAdmin />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/create-bot"
                  element={
                    <ProtectedRoute>
                      <CreateBot />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manage-bot/:botId"
                  element={
                    <ProtectedRoute>
                      <ManageBot />
                    </ProtectedRoute>
                  }
                >
                  <Route path="settings" element={<UpdateBot />} />
                  <Route path="welcome" element={<WelcomeMessage />} />
                  <Route path="payment-plans" element={<PaymentPlans />} />
                  <Route path="redirect" element={<RedirectButtons />} />
                  <Route path="commands" element={<BotCommands />} />
                  <Route path="administrators" element={<BotAdministrators />} />
                  <Route path="telegram-groups" element={<BotTelegramGroups />} />
                  <Route path="botfather" element={<BotFather />} />
                  <Route path="warning-messages" element={<WarningMessages />} />
                  <Route path="admin-commands" element={<AdminCommands />} />
                </Route>
                <Route
                  path="/bot-list"
                  element={
                    <ProtectedRoute>
                      <BotList />
                    </ProtectedRoute>
                  }
                />
                {/* Marketing routes - both with and without botId */}
                <Route
                  path="/marketing"
                  element={
                    <ProtectedRoute>
                      <Marketing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing/:botId"
                  element={
                    <ProtectedRoute>
                      <Marketing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing/alerts"
                  element={
                    <ProtectedRoute>
                      <Alerts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing/downsell"
                  element={
                    <ProtectedRoute>
                      <Downsell />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing/tracking-links"
                  element={
                    <ProtectedRoute>
                      <TrackingLinks />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/results/contacts"
                  element={
                    <ProtectedRoute>
                      <Contacts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contact-details/:contactId"
                  element={
                    <ProtectedRoute>
                      <ContactDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/users"
                  element={
                    <ProtectedRoute>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/logs"
                  element={
                    <ProtectedRoute>
                      <Logs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/payment-cycles"
                  element={
                    <ProtectedRoute>
                      <PaymentCycles />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/payment-gateways"
                  element={
                    <ProtectedRoute>
                      <PaymentGatewayConfigs />
                    </ProtectedRoute>
                  }
                />
                {/* Group management - with optional botId param */}
                <Route
                  path="/group-management"
                  element={
                    <ProtectedRoute>
                      <GroupManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/group-management/:botId"
                  element={
                    <ProtectedRoute>
                      <GroupManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <ProtectedRoute>
                      <Billing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/user-groups"
                  element={
                    <ProtectedRoute>
                      <UserGroups />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/security"
                  element={
                    <ProtectedRoute>
                      <SecuritySettings />
                    </ProtectedRoute>
                  }
                />
                {/* FTP Manager - both paths for compatibility with sidebar link */}
                <Route
                  path="/settings/ftp-manager"
                  element={
                    <ProtectedRoute>
                      <FtpManager />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ftp"
                  element={
                    <Navigate to="/settings/ftp-manager" replace />
                  }
                />
                {/* Public route - Telegram users are not logged in */}
                <Route path="/payment/card/:token" element={<CardPayment />} />
                {/* Payment Status - uses botId param (sidebar sends selectedBotId) */}
                <Route
                  path="/payment-status"
                  element={
                    <ProtectedRoute>
                      <PaymentStatus />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-status/:botId"
                  element={
                    <ProtectedRoute>
                      <PaymentStatus />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/storage"
                  element={
                    <ProtectedRoute>
                      <StorageSettings />
                    </ProtectedRoute>
                  }
                />
                {/* Artisan Commands - both paths for compatibility with sidebar link */}
                <Route
                  path="/settings/artisan-commands"
                  element={
                    <ProtectedRoute>
                      <ArtisanCommands />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/artisan"
                  element={
                    <ProtectedRoute>
                      <ArtisanCommands />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/cron-jobs"
                  element={
                    <ProtectedRoute>
                      <CronJobs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/warning-messages"
                  element={
                    <ProtectedRoute>
                      <WarningMessages />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/laravel-logs"
                  element={
                    <ProtectedRoute>
                      <LaravelLogs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/translations"
                  element={
                    <ProtectedRoute>
                      <Translations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                {/* Catch-all 404 route - must be last */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </Router>
        </ErrorBoundary>
      </AuthProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
